// bodyText.ts — Pure string helper for converting legacy seed post bodies to plain text.
// No React import; safe to use outside component trees (e.g. ReportModal preview).

import { splitOnLinks } from './linkify';

/**
 * Convert a legacy seed post body to a plain text string — no DOM required.
 *
 * Rules applied in order:
 *   1. Replace <br> / <br/> / <br /> with a single space.
 *   2. Replace <a class="tag" …>#X</a> with its inner text (#X).
 *   3. Tokenize http(s) links with the SAME matcher PostBody uses to build its
 *      anchors (linkify.ts), and pass each link run through untouched — so the
 *      URL a reporter sees in the ReportModal preview is byte-identical to the
 *      href the reader clicked. Only the non-link runs get tag-stripped, which
 *      also means a stray tag can never be spliced into the middle of a URL.
 *   4. Collapse runs of whitespace to a single space and trim.
 */
export function bodyToPlainText(body: string): string {
  const withoutMarkupWeKnow = body
    // 1. Line-break tags → space
    .replace(/<br\s*\/?>/gi, ' ')
    // 2. Hashtag anchors → inner text
    .replace(/<a\s[^>]*class=["']tag["'][^>]*>(.*?)<\/a>/gi, '$1');

  return (
    splitOnLinks(withoutMarkupWeKnow)
      // 3. Links verbatim; residual tags stripped from everything else
      .map((seg) => (seg.href ? seg.text : seg.text.replace(/<[^>]+>/g, '')))
      .join('')
      // 4. Collapse whitespace
      .replace(/\s+/g, ' ')
      .trim()
  );
}
